import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import Header from "./layout/Header";
import Router from "./Router";
import SidebarMenu from "./layout/SidebarMenu";
import Breadcrumb from "./layout/Breadcrumb";
import "./assets/styles/app.scss";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "oidc-react";
import { getProfileData } from "./utils/utils";
import { Toast } from "./components/common/Toast";
import FeedbackDialog from "components/feedback/FeedbackDialog";
import { NetworkProvider, useNetworkContext } from "./context/NetworkContext";
import { lightTheme } from "assets/themes/lightTheme";
import { darkTheme } from "assets/themes/darkTheme";
import { useThemeMode } from "context/ThemeContextProvider";
import AuthConfigurationError from "pages/AuthConfigurationError";
import oidcConfig, { hasRequiredOidcConfig } from "services/oidcConfig";
import { appShellLoaderSx, appShellOverlaySx, getAppShellMainSx, getAppShellRootSx } from "./appShellStyles";
import { ShellLayoutProvider, useShellLayout } from "layout/ShellLayoutContext";
import { hideLoader } from "components/common/Loader";
import { getPageInfoContent } from "components/common/pageInfoContent";

function UnauthorizedEventHandler() {
  const auth = useAuth();
  const isSignInStarted = useRef(false);

  useEffect(() => {
    const resetSignInGuard = (error: unknown) => {
      console.error("OIDC sign-in failed after unauthorized API response", error);
      isSignInStarted.current = false;
    };

    const handleUnauthorized = () => {
      if (auth.isLoading || isSignInStarted.current) {
        return;
      }

      isSignInStarted.current = true;

      try {
        void Promise.resolve(auth.signIn()).catch(resetSignInGuard);
      } catch (error) {
        resetSignInGuard(error);
      }
    };

    window.addEventListener("app:unauthorized", handleUnauthorized);

    return () => {
      window.removeEventListener("app:unauthorized", handleUnauthorized);
    };
  }, [auth]);

  useEffect(() => {
    if (!auth.isLoading) {
      isSignInStarted.current = false;
    }
  }, [auth.isLoading]);

  return null;
}

function AppShellContent({
  profileName,
  onLoginStatusChange,
}: {
  profileName: string;
  onLoginStatusChange: (status: boolean) => void;
}) {
  const { userRole } = useNetworkContext();
  const { sidebarWidth } = useShellLayout();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isPageInfoOpen, setIsPageInfoOpen] = useState(false);
  const { pathname } = useLocation();
  const hideNavigation = !userRole || userRole === "no-access" || userRole === "new-user";
  const activeSidebarWidth = hideNavigation ? 0 : sidebarWidth;
  const isActualsRoute = pathname === "/" || pathname === "/actuals";
  const pageInfo = useMemo(() => getPageInfoContent(pathname), [pathname]);

  useEffect(() => {
    hideLoader();
    setIsPageInfoOpen(false);
  }, [pathname]);

  return (
    <Box sx={getAppShellRootSx}>
      <Box id="overlay" sx={appShellOverlaySx} />
      <Box id="loader" sx={appShellLoaderSx} />
      <Header
        profileName={profileName}
        isFeedbackOpen={isFeedbackOpen}
        isPageInfoOpen={isPageInfoOpen}
        pageInfo={pageInfo}
        onFeedbackOpen={() => setIsFeedbackOpen(true)}
        onPageInfoClose={() => setIsPageInfoOpen(false)}
        onPageInfoOpen={() => setIsPageInfoOpen(true)}
      />
      {!hideNavigation && <SidebarMenu />}
      <Breadcrumb />
      <Box
        component="main"
        className="main-container"
        sx={getAppShellMainSx(activeSidebarWidth, { topPadding: isActualsRoute ? 6 : undefined })}
      >
        <Router onLoginStatusChange={onLoginStatusChange} />
      </Box>
      <FeedbackDialog open={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      <Toast />
    </Box>
  );
}

function AppShell({
  profileName,
  onLoginStatusChange,
}: {
  profileName: string;
  onLoginStatusChange: (status: boolean) => void;
}) {
  return (
    <BrowserRouter>
      <ShellLayoutProvider>
        <AppShellContent profileName={profileName} onLoginStatusChange={onLoginStatusChange} />
      </ShellLayoutProvider>
    </BrowserRouter>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { mode } = useThemeMode();
  const profile = isLoggedIn ? getProfileData() : null;
  const profileName = profile ? `${profile.given_name} (${profile.preferred_username})` : "";
  const theme = mode === "light" ? lightTheme : darkTheme;

  if (!hasRequiredOidcConfig) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthConfigurationError />
        <Toast />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider {...oidcConfig}>
        <UnauthorizedEventHandler />
        <NetworkProvider>
          <AppShell profileName={profileName} onLoginStatusChange={setIsLoggedIn} />
        </NetworkProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
